/* 
  Defines a LoadingStatus object with four static properties: failed, idle, loading, and succeeded.
  The statuses indicates the state of an asynchronous operation.

  It provides the methods for creating and retrieving instances of the object.
*/
export default class LoadingStatus {
  /**
   * This is a static getter function that returns an instance of the "Failed" loading status.
   * This status indicates the asynchronous operation has finished but returned an error.
   *
   * @returns it returns the instance of the class with the name `'failed'`.
   */
  static get Failed() {
    return this.getInstance('failed');
  }

  /**
   * This is a static getter function that returns an instance of the "Idle" loading status.
   * This status indicates the asynchronous operation is not started.
   *
   * @returns it returns the instance of the class with the name `'idle'`.
   */
  static get Idle() {
    return this.getInstance('idle');
  }

  /**
   * This is a static getter function that returns an instance of the "Loading" loading status.
   * This status indicates the asynchronous operation has started but it hasn't finished.
   *
   * @returns it returns the instance of the class with the name `'loading'`.
   */
  static get Loading() {
    return this.getInstance('loading');
  }

  /**
   * This is a static getter function that returns an instance of the "Succeeded" loading status.
   * This status indicates the asynchronous operation has finished without errors.
   *
   * @returns it returns the instance of the class with the name `'succeeded'`.
   */
  static get Succeeded() {
    return this.getInstance('succeeded');
  }

  static fromKey(key) {
    const statusMap = {
      failed: LoadingStatus.Failed,
      idle: LoadingStatus.Idle,
      loading: LoadingStatus.Loading,
      succeeded: LoadingStatus.Succeeded,
    };
    return statusMap[key];
  }

  /**
   * This function returns an instance of the LoadingStatus class based on a given key, creating a new
   * instance if one does not already exist.
   *
   * @param key - a unique identifier used to create and retrieve instances of the LoadingStatus class.
   * It is used to ensure that only one instance of the LoadingStatus class is created for each unique key value.
   *
   * @returns returns an instance of the `LoadingStatus` class with the specified `key`.
   * If an instance with the specified `key` already exists, it returns that instance instead of creating a new one.
   */
  static getInstance(key) {
    if (!this.instances[key]) {
      this.instances[key] = new LoadingStatus(key);
    }
    return this.instances[key];
  }

  constructor(name) {
    this.name = name;
  }

  toString() {
    return `${this.name}`;
  }
}

Object.defineProperty(LoadingStatus, 'instances', { value: {} });
